import { useContext, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { SettingsContext, UserContext } from "../../../contexts";
import { ShareHelper } from "../../../helpers";
import {
  useAcceptedByClient,
  useApproveQuote,
  useCreateListing,
  useSendQuote,
  useUpdateListing,
  useUpsertQuote,
} from "../../hooks/quotes";

/**
 * The quote workspace's ONE next step + its secondary actions, derived from the
 * quote's stage. Same calls, same order of effects and same `onQuoteSaved`
 * reporting as the CHOOSE ACTION components it replaces (ReviewerActions,
 * ApproveQuote, ApprovedActions, CreateListing, BroadcastListing) — only the
 * presentation changes, so the DAO setup and this one can never drift apart in
 * what an action actually does.
 */

// The journey a quote walks, in order. `listed` is not a status: it is
// accepted_by_client with a listing behind it.
export const QUOTE_STAGES = [
  { id: "created", label: "Draft" },
  { id: "to_review", label: "Review" },
  { id: "approved", label: "Approved" },
  { id: "quote_sent", label: "Sent" },
  { id: "accepted_by_client", label: "Accepted" },
  { id: "listed", label: "Listed" },
];

export const stageOf = (quote) =>
  quote?.status === "accepted_by_client" && quote?.listing ? "listed" : quote?.status || "created";

const listingUrl = (listingId, full = false) =>
  `${full ? window.location.origin : ""}/product/${listingId}`;

export default function useQuoteActions(quote, onQuoteSaved) {
  const { user } = useContext(UserContext);
  const settings = useContext(SettingsContext);
  const history = useHistory();
  const { approveQuote, status: approveStatus } = useApproveQuote();
  const { sendQuote, status: sendStatus } = useSendQuote();
  const { acceptedByClient, status: acceptStatus } = useAcceptedByClient();
  const { createListing, status: createStatus } = useCreateListing();
  const { updateListing, status: updateStatus } = useUpdateListing();
  const { upsertQuote } = useUpsertQuote();
  // Which inline tray is open under the action bar: "assign" | "send" | "listing".
  // Actions that open one carry `panel`, so a host without trays (the quote
  // list rows) can open the quote instead.
  const [panel, setPanel] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!quote?.id) {
    return { stage: "created", primary: null, secondary: [], panel, setPanel };
  }

  const isOrder = quote.type === "order";
  const stage = stageOf(quote);
  const done = (updated) => onQuoteSaved?.(updated ?? quote);
  const togglePanel = (name) => setPanel((p) => (p === name ? null : name));

  // ApproveQuote's rule, verbatim.
  const canApprove = (!quote.reviewer && user?.is_admin) || quote?.reviewer?.id === user?.id;

  const approve = {
    id: "approve",
    label: "Approve",
    icon: "CheckCircle",
    loading: approveStatus === "loading",
    onClick: async () => {
      await approveQuote(quote.id);
      done(quote);
    },
  };
  const assign = {
    id: "assign",
    label: quote.reviewer ? "Change reviewer" : "Assign reviewer",
    icon: "User",
    panel: "assign",
    onClick: () => togglePanel("assign"),
  };
  const send = async (type) => {
    const updated = await sendQuote({ id: quote.id, type });
    setPanel(null);
    done(updated);
  };
  // A sell quote is sent as SP, Consign or both — chosen in the inline tray.
  const sendAction = (label) =>
    isOrder
      ? { id: "send", label, icon: "Send", loading: sendStatus === "loading", onClick: () => send() }
      : { id: "send", label, icon: "Send", panel: "send", onClick: () => togglePanel("send") };
  const negotiate = {
    id: "negotiate",
    label: "Move to negotiation",
    icon: "MessageCircle",
    onClick: () => send("MOVE_NEGOTIATION"),
  };
  const accepted = {
    id: "accepted",
    label: "Client accepted",
    icon: "Check",
    loading: acceptStatus === "loading",
    onClick: async () => done(await acceptedByClient(quote.id)),
  };
  const createListingAction = (reportBack) => ({
    id: "create-listing",
    label: "Create listing",
    icon: "Plus",
    loading: createStatus === "loading",
    onClick: async () => {
      const updated = await createListing(quote.id);
      // ApprovedActions reports back; the accepted-stage CreateListing never did.
      if (reportBack) done(updated);
    },
  });
  const updateListingAction = (reportBack) => ({
    id: "update-listing",
    label: "Update listing",
    icon: "RefreshCw",
    loading: updateStatus === "loading",
    onClick: async () => {
      const updated = await updateListing(quote);
      if (reportBack) done(updated);
    },
  });
  const changeListing = {
    id: "change-listing",
    label: "Change listing",
    icon: "Package",
    panel: "listing",
    onClick: () => togglePanel("listing"),
  };

  // ApprovedActions' listing rule: a linked listing can only be updated or re-pointed.
  const listingActions = quote.listing_id
    ? [updateListingAction(true), changeListing]
    : [createListingAction(true)];

  let primary = null;
  let secondary = [];
  let note = null;

  switch (stage) {
    case "created":
    case "to_review":
      if (canApprove) {
        primary = approve;
      } else {
        note = `Waiting for ${[quote.reviewer?.first_name, quote.reviewer?.last_name].filter(Boolean).join(" ")}`;
      }
      secondary = [assign];
      break;
    case "approved":
      primary = sendAction("Send quote");
      secondary = [...(isOrder ? [negotiate] : []), accepted, ...listingActions];
      break;
    case "quote_sent":
      primary = accepted;
      secondary = [sendAction("Send again"), ...(isOrder ? [negotiate] : []), ...listingActions];
      break;
    case "accepted_by_client":
      primary = createListingAction(false);
      break;
    case "listed": {
      const draft = ["new", "submitted"].includes(quote.listing?.status);
      const client = quote.request?.user;
      const reachable = client && !client.is_admin;
      const viewListing = {
        id: "view-listing",
        label: "View listing",
        icon: "ExternalLink",
        onClick: () => history.push(`${listingUrl(quote.listing.id)}?${settings.iframeQueryParams}`),
      };
      if (draft) {
        primary = viewListing;
      } else {
        primary = updateListingAction(false);
        secondary = [
          {
            id: "whatsapp",
            label: "WhatsApp",
            icon: "MessageCircle",
            disabled: !(reachable && client.phone),
            onClick: () =>
              ShareHelper.whatsapp(
                `${listingUrl(quote.listing.id, true)}?${settings.iframeQueryParams}`,
                client.phone
              ),
          },
          {
            id: "email",
            label: "Email",
            icon: "Mail",
            disabled: !(reachable && client.email),
            onClick: () => axios.post(`/api/quotes/${quote.id}/send-listing-email`),
          },
          {
            id: "copy",
            label: copied ? "Copied" : "Copy link",
            icon: "Copy",
            onClick: () => {
              navigator.clipboard.writeText(
                `${listingUrl(quote.listing.id, true)}?${settings.iframeQueryParams}`
              );
              setCopied(true);
              setTimeout(() => setCopied(false), 3000);
            },
          },
        ];
      }
      break;
    }
    default:
      break;
  }

  // CHANGE LISTING, as ApprovedActions.onListingChosen does it.
  const chooseListing = async (listing) => {
    setIsLinking(true);
    try {
      const linkedQuote = { ...quote, listing_id: listing.id };
      await upsertQuote({ id: quote.id, quoteData: linkedQuote });
      await updateListing(linkedQuote);
      done(linkedQuote);
      setPanel(null);
    } finally {
      setIsLinking(false);
    }
  };

  return {
    stage,
    primary,
    secondary,
    note,
    panel,
    setPanel,
    send,
    sending: sendStatus === "loading",
    chooseListing,
    isLinking,
  };
}
